import { useCallback, useMemo } from 'react';
import type { InstanceProps, SvgMarkerSetExtendedProps } from '../types';
import { useSvgData } from './SvgData';
import { InstancedObject } from './InstancedObject';
import { type ThreeEvent } from '@react-three/fiber';

export const SvgMarkerSet = ({
  id,
  normalSvg: normalSvgProps,
  hoveredSvg: hoveredSvgProps,
  instances: markerInstances,
  enableClippingByVisibilityBox,
  enableFilteringByVisibilityBox = true,
  visibilityBox,
}: SvgMarkerSetExtendedProps) => {
  const shouldHandleClick = useCallback((event: ThreeEvent<PointerEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event.nativeEvent;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const normalSvg = useSvgData(normalSvgProps, enableClippingByVisibilityBox);
  const hoveredSvg = useSvgData(hoveredSvgProps, enableClippingByVisibilityBox);

  const isReady = normalSvg && (!hoveredSvgProps || hoveredSvg);

  const vboxFilter = enableFilteringByVisibilityBox ? visibilityBox : undefined;

  const instances: InstanceProps[] = useMemo(
    () =>
      (vboxFilter
        ? markerInstances.filter(({ position }) => vboxFilter.checkIsInside(position))
        : markerInstances
      ).map((current) => {
        const { onPointerOver, onPointerOut, onClick } = current;
        return {
          ...current,
          scale: current.normalScale,
          onPointerOver: () => {
            onPointerOver?.();
            return {
              scale: current.hoveredScale,
            };
          },
          onPointerOut,
          onClick,
        };
      }),
    [markerInstances, vboxFilter]
  );

  const instancedObject = useMemo(
    () =>
      isReady && (
        <InstancedObject
          id={id}
          meshes={normalSvg.meshes}
          hoveredMeshes={hoveredSvg?.meshes}
          instances={instances}
          shouldHandleClick={shouldHandleClick}
          isBillboard={true}
        />
      ),
    [isReady, id, normalSvg, hoveredSvg, instances, shouldHandleClick]
  );

  return <>{instancedObject}</>;
};
